import { useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';

import Button from 'honeydew/Button';
import { trackEvent } from 'utils/analyticsUtils';

/**
 * CopyReferralCode component displays an input field containing a referral code or link,
 * alongside a button that allows users to copy the code/link to the clipboard.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.buttonCopy - The label text to display on the copy button.
 * @param {string} props.buttonSize - The size of the copy button.
 * @param {string} props.code - The referral code to be copied.
 * @param {boolean} props.inputBg - Whether to use the special input background style.
 * @param {string} props.link - The referral link to be copied (used if code is empty).
 * @param {function} [props.onCopy] - Optional callback fired when the code/link is copied.
 * @returns {JSX.Element} Rendered CopyReferralCode component.
 */
function CopyReferralCode({ buttonCopy = 'Copy', buttonSize = 'lg', code = '', inputBg = false, link, onCopy = null }) {
    const [linkCopied, setLinkCopied] = useState(false);

    const placeholderInputText = code !== '' ? code : link;

    const handleCopyClick = () => {
        const textToCopy = code || link;
        navigator.clipboard.writeText(textToCopy);

        if (onCopy) {
            onCopy();
        } else {
            trackEvent(code ? 'referral code copy' : 'referral link copy');
        }

        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
    };

    return (
        <>
            <input
                name="link"
                value={placeholderInputText}
                readOnly
                className={clsx(
                    'h-[56px] w-full rounded border px-20 transition-all duration-100 hover:bg-off-white',
                    { 'border-none bg-off-white text-center font-grotesk-bold text-black': inputBg },
                    { 'border-black text-grey': !inputBg }
                )}
            />

            <Button size={buttonSize} variant={linkCopied ? 'primary' : 'secondary'} onClick={handleCopyClick}>
                {linkCopied ? (
                    <div className="flex items-center">
                        <CheckIcon className="mr-10 inline-block h-20 w-20" /> Copied
                    </div>
                ) : (
                    <span className="whitespace-nowrap">{buttonCopy}</span>
                )}
            </Button>
        </>
    );
}

export default CopyReferralCode;
